import React, { type Reducer } from 'react';
import Add from '@mui/icons-material/Add';
import Remove from '@mui/icons-material/Remove';
import { IconButton } from '@mui/material';
import { BinSecondary } from '@shared/assets';
import * as st from './QuantityInput.module.scss';

type Props = {
  value: string;
  setValue: (value: string) => void;
  maxQuantity?: number;
  defaultValue?: string;
  onDelete?: () => void;
};

enum Action {
  PLUS = 'PLUS',
  MINUS = 'MINUS',
  INPUT = 'INPUT',
}

interface CountAction {
  type: Action;
  payload?: string;
}

type State = {
  count: string | undefined;
};

const QuantityInput: React.FC<Props> = ({
  maxQuantity = Infinity,
  value = '0',
  setValue,
  defaultValue = '1',
  onDelete,
}) => {
  const reducer: Reducer<State, CountAction> = (state, action) => {
    const count = state.count ? parseInt(state.count) : 0;

    switch (action.type) {
      case Action.PLUS:
        return {
          ...state,
          count: count >= maxQuantity ? (count + 0).toString() : (count + 1).toString(),
        };

      case Action.MINUS:
        return {
          ...state,
          count: count > 0 ? (count - 1).toString() : '0',
        };
      case Action.INPUT:
        return {
          ...state,
          count: action.payload,
        };
      default: {
        return state;
      }
    }
  };

  const [state, dispatch] = React.useReducer(reducer, {
    count: value,
  });

  const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => {
    let quantity = e.target.value ? parseInt(e.target.value) : '0';
    if (typeof quantity === 'number' && quantity > maxQuantity) {
      quantity = maxQuantity;
    }
    dispatch({ type: Action.INPUT, payload: quantity.toString() });
  };

  React.useEffect(() => {
    state.count && setValue(state.count);
  }, [state.count, setValue]);

  React.useEffect(() => {
    value === defaultValue && dispatch({ type: Action.INPUT, payload: defaultValue });
  }, [value, defaultValue]);

  return (
    <div className={st.wrapper}>
      <div className={st.button}>
        {+state!.count! === 1 &&
          (location.pathname.endsWith('cart/') || location.pathname.endsWith('cart')) && (
            <IconButton
              onClick={() => {
                onDelete?.();
              }}
            >
              <BinSecondary />
            </IconButton>
          )}

        {+state!.count! <= 1 && location.pathname.indexOf('product/') !== -1 && (
          <IconButton>
            <Remove />
          </IconButton>
        )}

        {+state!.count! > 1 && (
          <IconButton onClick={() => dispatch({ type: Action.MINUS })}>
            <Remove />
          </IconButton>
        )}
      </div>
      <input
        className={st.input}
        value={state.count}
        min="1"
        onChange={handleChange}
        type="number"
      />
      <div className={st.button}>
        <IconButton onClick={() => dispatch({ type: Action.PLUS })}>
          <Add />
        </IconButton>
      </div>
    </div>
  );
};

export default QuantityInput;
